'use client';

import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { useJsApiLoader } from '@react-google-maps/api';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';

// components
import Skeleton from 'react-loading-skeleton';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { MlMapCard } from '@/components/molecules/ml-map-card/ml-map-card';
import { MlStudioList } from '@/components/molecules/ml-studio-list/ml-studio-list';

// data
import { getGeoJson } from '../../../lib/contentstack-sdk/server-actions';

// hooks
import { useLocation } from '@/context/locationContext';

// types
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IDesignStudio } from '../or-design-studio-block/or-design-studio-block.types';
import { MapSearchWrapper } from './search/map-search-wrapper';

const GOOGLE_MAPS_LIBS: ('core' | 'marker')[] = ['core', 'marker'];
const INITIAL_MAP_CENTER = { lat: 37, lng: -82.6 };
const INITIAL_MAP_ZOOM = 5.5;
const SELECTED_ZOOM = 9;

export const OrMappingTool = (): JSX.Element => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries: GOOGLE_MAPS_LIBS,
  });

  // @TODO get studio Data from new useInitialProps()
  const { allLocations, allStudios, getStudioById, currentBuildStudio } =
    useLocation();

  const [hoveredStudioId, setHoveredStudioId] = useState<number | null>(null);
  const [selectedStudioId, setSelectedStudioId] = useState<number | null>(null);
  const [selectedStudioData, setSelectedStudioData] =
    useState<IDesignStudio | null>(null);

  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const mapRef = useRef<google.maps.Map | null>(null);
  const markerMap = useRef(
    new Map<
      number,
      {
        marker: google.maps.marker.AdvancedMarkerElement;
        root: Root;
      }
    >(),
  );
  const dataLayerRef = useRef<google.maps.Data | null>(null);

  const handleMapCardClose = () => {
    setSelectedStudioData(null);
    setSelectedStudioId(null);
  };

  const [designCenters, setDesignCenters] = useState<
    { studio: IDesignStudio; position: { lat: number; lng: number } }[]
  >([]);

  useEffect(() => {
    if (!isLoaded || allStudios.length === 0) return;
    const fetchGeocodedPins = async () => {
      try {
        const centersWithPins = await Promise.allSettled(
          allStudios.map(async (studio: IDesignStudio) => {
            const lat = studio?.lat;
            const long = studio?.long;
            if (!lat || !long)
              return {
                studio,
                position: {
                  lat: 0,
                  lng: 0,
                },
              };

            return {
              studio,
              position: {
                lat: lat,
                lng: long,
              },
            };
          }),
        );
        const filteredPins = centersWithPins
          .filter(
            (
              result,
            ): result is PromiseFulfilledResult<{
              studio: IDesignStudio;
              position: { lat: number; lng: number };
            }> => result.status === 'fulfilled' && result.value !== null,
          )
          .map((result) => result.value);
        setDesignCenters(filteredPins);
      } catch (error) {
        console.error('Error during geocoding:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    fetchGeocodedPins();
  }, [allStudios, isLoaded]);

  const selectedPin = useMemo(() => {
    return designCenters.find((p) => p.studio.Id === selectedStudioId) || null;
  }, [designCenters, selectedStudioId]);

  const resetMapView = () => {
    const map = mapRef.current;
    const layer = dataLayerRef.current;
    if (!map || !layer) return;

    map.panTo(INITIAL_MAP_CENTER);
    setTimeout(() => {
      map.setZoom(INITIAL_MAP_ZOOM);
    }, 200);

    layer.setStyle({
      fillColor: 'var(--color-terra-cotta-400)',
      fillOpacity: 0.25,
      strokeWeight: 0,
      visible: true,
      zIndex: 1,
    });
  };

  const renderMapIcon = ({
    studioId,
    isActive,
    root,
  }: {
    studioId: number;
    isActive: Boolean;
    root: Root;
  }) => {
    const handlePinClick = () => {
      setSelectedStudioId(studioId);
    };

    root.render(
      <div
        className="text-32"
        style={{ pointerEvents: 'auto' }}
        onMouseEnter={() => setHoveredStudioId(studioId)}
        onMouseLeave={() => setHoveredStudioId(null)}
        onClick={handlePinClick}
      >
        {isActive ? (
          <AtIcon icon={EIcons.LOCATION_SELECTED} />
        ) : (
          <AtIcon icon={EIcons.LOCATION} color="var(--color-terra-cotta-400)" />
        )}
      </div>,
    );
  };

  // load map + create build studio pins
  useEffect(() => {
    const mapDiv = document.getElementById('map');
    if (!isLoaded || !mapDiv || designCenters.length === 0) return;

    async function initMap(mapDiv: HTMLElement) {
      const mapInstance = new google.maps.Map(mapDiv, {
        center: INITIAL_MAP_CENTER,
        zoom: INITIAL_MAP_ZOOM,
        mapId: 'map',
        disableDefaultUI: true,
      });

      mapRef.current = mapInstance;
      markerMap.current.clear();

      const geoJson = await getGeoJson();
      if (geoJson) {
        mapInstance.data.addGeoJson(geoJson.ServiceArea);
      }

      dataLayerRef.current = mapInstance.data;

      mapInstance.data.setStyle({
        fillColor: 'var(--neutral-600)',
        fillOpacity: 0.15,
        strokeWeight: 0,
        visible: true,
        zIndex: 1,
      });

      const { AdvancedMarkerElement } = (await google.maps.importLibrary(
        'marker',
      )) as google.maps.MarkerLibrary;

      designCenters.forEach((pin) => {
        const markerDiv = document.createElement('div');
        const iconContainer = document.createElement('div');
        markerDiv.appendChild(iconContainer);

        const root = createRoot(iconContainer);
        // Active here is current studio, if no studio selected return false
        const isActive = pin.studio.Id === currentBuildStudio?.Id;
        renderMapIcon({ studioId: pin.studio.Id, isActive, root });

        const marker = new AdvancedMarkerElement({
          map: mapInstance,
          position: pin.position,
          content: markerDiv,
        });

        markerMap.current.set(pin.studio.Id, { marker, root });
      });
    }

    initMap(mapDiv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, designCenters]);

  useEffect(() => {
    markerMap.current.forEach(({ root }, studioId) => {
      const isActive = studioId === selectedStudioId;
      renderMapIcon({ studioId, isActive, root });
    });
  }, [selectedStudioId]);
  // update build studio pin icon if hovered or selected
  useEffect(() => {
    designCenters.forEach((pin) => {
      const entry = markerMap.current.get(pin.studio.Id);
      if (!entry) return;

      const isActive =
        hoveredStudioId === pin.studio.Id || selectedStudioId === pin.studio.Id;

      renderMapIcon({ studioId: pin.studio.Id, isActive, root: entry.root });
    });
  }, [hoveredStudioId, selectedStudioId, designCenters]);

  // highlight build area if pin is hovered
  useEffect(() => {
    const layer = dataLayerRef.current;
    if (!layer) return;

    layer.setStyle((feature) => {
      const isMatch = feature.getProperty('StudioId') === hoveredStudioId;

      return {
        fillColor: 'var(--neutral-400)',
        fillOpacity: isMatch ? 0.35 : 0.15,
        strokeWeight: 0,
        strokeColor: undefined,
        visible: true,
        zIndex: isMatch ? 2 : 1,
      };
    });
  }, [hoveredStudioId]);

  // set selectedStudioId if user has location set
  useEffect(() => {
    if (!designCenters.length) return;

    if (currentBuildStudio?.Id) {
      setSelectedStudioId(currentBuildStudio.Id);
      setIsInitializing(false);
      return;
    }

    setIsInitializing(false);
  }, [designCenters, currentBuildStudio?.Id]);

  // zoom in on selected build studio
  useEffect(() => {
    if (!selectedStudioId) return;

    const map = mapRef.current;
    if (!map) return;

    const pin = selectedPin;
    if (!pin) return;

    const fetchDesignStudio = async () => {
      try {
        if (!selectedStudioId) {
          console.warn('No selectedStudioId provided');
          return;
        }

        const studio = getStudioById(Number(selectedStudioId));

        if (!studio) {
          console.warn(`No studio found for ID: ${selectedStudioId}`);
          return;
        }

        const response = await fetch(
          `/algolia/home-plans?design_studio_id=${selectedStudioId}&design_studio=${encodeURIComponent(JSON.stringify(studio))}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const fetchedDesignStudio = await response.json();
        setSelectedStudioData(fetchedDesignStudio);
      } catch (error) {
        console.error('Error fetching design studio:', error);
      }
    };

    fetchDesignStudio();

    map.panTo(pin.position);
    setTimeout(() => {
      map.setZoom(SELECTED_ZOOM);
    }, 200);
  }, [selectedStudioId, getStudioById, selectedPin]);

  // reset map when map card is closed
  useEffect(() => {
    if (selectedStudioId === null) {
      resetMapView();
    }
  }, [selectedStudioId]);

  const handleSearchSelection = (studioId: number | null) => {
    if (!studioId) return;

    const matchingPin = designCenters.find((pin) => {
      return pin.studio.Id === studioId;
    });

    if (matchingPin) {
      setSelectedStudioId(matchingPin.studio.Id);
    } else {
      console.warn('No matching design center for studio ID:', studioId);
    }
  };

  return (
    <>
      {!isLoaded || isInitializing ? (
        <Skeleton
          baseColor="var(--color-neutral-100)"
          highlightColor="var(--color-neutral-200)"
          height={200}
          className="rounded-30"
        />
      ) : (
        <div className="flex w-full flex-col justify-between md:flex-row">
          <div
            // google maps isn't screenreader friendly
            aria-hidden="true"
            role="application"
            id="map"
            data-testid="map-container"
            className="mb-10 h-500 w-full rounded-3xl md:mb-0 lg:h-650 lg:w-2/3"
          />

          <div className="w-full md:ml-5 md:w-598 lg:ml-24">
            <AnimatePresence mode="wait">
              {!selectedStudioData ? (
                <motion.div
                  key="list"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-10">
                    <MapSearchWrapper
                      locationData={allLocations}
                      onFocusChange={setIsSearchFocused}
                      onElementClick={handleSearchSelection}
                    />
                  </div>

                  <span className="sr-only" role="status" aria-live="polite">
                    {isSearchFocused &&
                      'Studio list is temporarily disabled while search is active.'}
                  </span>

                  <div className="relative" aria-disabled={isSearchFocused}>
                    <div
                      aria-hidden={isSearchFocused ? 'true' : 'false'}
                      className={classNames(
                        'transition-opacity duration-300 ease-in',
                        'opacity-100',
                        isSearchFocused && 'pointer-events-none opacity-50',
                      )}
                    >
                      <MlStudioList
                        allStudios={allStudios}
                        onItemClick={(id) => setSelectedStudioId(id)}
                        onItemHover={(id) => setHoveredStudioId(id)}
                      />
                    </div>
                    {isSearchFocused && (
                      <div
                        aria-hidden={true}
                        className="z-2 absolute inset-0 bg-white opacity-75"
                      />
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="card"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <MlMapCard
                    studioData={selectedStudioData}
                    onClose={handleMapCardClose}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </>
  );
};
